/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `paymentStatusReducer` for the Caintra Connect frontend.
 * @module utils/payment/paymentStatusReducer
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
export const PAYMENT_STATUS_ACTIONS = {
  SUCCESS: "success",
  ERROR: "error",
  POLL: "poll",
};

export const DEFAULT_MAX_POLLING_ATTEMPTS = 60;

/**
 * Maps a charge status to the next polling action.
 * @param {string} status - Charge status from OpenPay/backend
 * @param {{ attemptNumber: number, popupWasClosed: boolean, maxAttempts?: number }} context
 * @returns {{ action: string, nextAttempt?: number }}
 */
export const resolvePaymentStatusAction = (
  status,
  { attemptNumber, popupWasClosed, maxAttempts = DEFAULT_MAX_POLLING_ATTEMPTS },
) => {
  switch (status) {
    case "completed":
      return { action: PAYMENT_STATUS_ACTIONS.SUCCESS };

    case "failed":
    case "cancelled":
      return { action: PAYMENT_STATUS_ACTIONS.ERROR };

    case "charge_pending":
    case "in_progress":
      if (popupWasClosed) {
        return { action: PAYMENT_STATUS_ACTIONS.ERROR };
      }
      if (attemptNumber >= maxAttempts) {
        return { action: PAYMENT_STATUS_ACTIONS.ERROR };
      }
      return {
        action: PAYMENT_STATUS_ACTIONS.POLL,
        nextAttempt: attemptNumber + 1,
      };

    default:
      return { action: PAYMENT_STATUS_ACTIONS.ERROR };
  }
};
